/* @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Panel, PanelGroup } from 'react-resizable-panels';

import { breakpoints, useMediaQuery } from '@coursera/cds-core';
import type { ResizeType } from '@coursera/event-pulse-types';
import { useTracker } from '@coursera/event-pulse/react';

import { useCoachPermissions } from 'bundles/ai-course-coach/utils/CoachPermissionWrapper';
import CourseToolBar from 'bundles/course-v2/components/navigation/CourseToolBar/CourseToolBar';
import SplitPanelResizeHandle from 'bundles/course-v2/components/navigation/CourseToolBar/SplitPanelResizeHandle';
import { useCourseToolBarPlugins } from 'bundles/course-v2/components/navigation/CourseToolBar/useCourseToolBarPlugins';
import { useCourseDataProvider } from 'bundles/course-v2/providers/CourseDataProvider';

const styles = {
  coachPanelOverrides: css`
    padding-left: var(--cds-spacing-200);
    background-color: var(--cds-color-neutral-background-primary-weak);
  `,
};

const MAX_SIZE = 50;

const WithResizableCourseToolBarWrapper = ({ children }: React.PropsWithChildren<{}>) => {
  const isScreenXlUp = useMediaQuery(breakpoints.up('xl'));
  const isScreenLgUp = useMediaQuery(breakpoints.up('lg'));
  const isScreenMdUp = useMediaQuery(breakpoints.up('md'));
  const [currentResizeType, setCurrentResizeType] = useState<ResizeType | null>(null);
  const panelWidthRef = useRef<number | null>(null); // tracks the current width of the panel updated on resize
  const { plugins, expanded } = useCourseToolBarPlugins();
  /* autoClose prevents the toolbar from being open and empty if toolbar is expanded on other pages,
   * but Coach (or other tool) is not available on the current page.
   * autoClose has timing built in to keep the toolbar open while the page is loading. If the page loads without tools,
   * it will close the course tool bar to avoid having an empty toolbar */
  const autoCloseTimer = useRef<null | NodeJS.Timeout>(null);
  const [autoClose, setAutoClose] = useState(false);

  const track = useTracker();
  const { courseId, courseName, courseSlug } = useCourseDataProvider();
  const { showEmbeddedCoach } = useCoachPermissions({ courseId });
  // Show the toolbar when there any tool is registered (e.g., flashcards),
  // even if Coach itself is disabled.
  const hasTools = plugins.length > 0;
  const shouldShowToolbar = (hasTools || showEmbeddedCoach) && expanded && !autoClose;
  const shouldShowResizableToolbar = isScreenMdUp && shouldShowToolbar;

  const handleAutoClose = React.useCallback<() => void>(() => {
    if (autoCloseTimer.current) {
      clearTimeout(autoCloseTimer.current);
    }

    autoCloseTimer.current = setTimeout(() => {
      setAutoClose(plugins.length === 0);
    }, 500);
  }, [plugins.length]);

  useEffect(() => {
    handleAutoClose();
  }, [handleAutoClose]);

  const panelSize = useMemo(() => {
    if (isScreenXlUp) {
      return 25;
    } else if (isScreenLgUp) {
      return 30;
    } else if (isScreenMdUp) {
      return 40;
    } else {
      return 50;
    }
  }, [isScreenXlUp, isScreenLgUp, isScreenMdUp]);

  const handleResizeEventing = React.useCallback<(resizeType: ResizeType) => void>(
    (resizeType) => {
      track('interact_course_toolbar_resize', {
        resizeType,
        ...(courseId && {
          product: {
            id: courseId,
            type: 'course',
            name: courseName,
            slug: courseSlug,
          },
        }),
      });
    },
    [courseId, courseName, courseSlug, track]
  );

  useEffect(() => {
    if (currentResizeType === 'resize_max' || currentResizeType === 'resize_min') {
      handleResizeEventing(currentResizeType);
    }
  }, [currentResizeType, handleResizeEventing]);

  // we only want to call eventing when the pointer is released, indicating the resize is done,
  // and not on every resize which will get noisy
  // note: this callback does not get called for the max and min sizes, so we handle those in the useEffect above
  const handleResizeHandlePointerUp = React.useCallback<() => void>(() => {
    if (currentResizeType) {
      handleResizeEventing(currentResizeType);
    }
  }, [currentResizeType, handleResizeEventing]);

  const handlePanelResize = React.useCallback<(width: number) => void>(
    (width) => {
      const didResize = panelWidthRef.current !== null && width !== panelWidthRef.current;
      panelWidthRef.current = width;

      // this callback gets called on mount and re-renders, but we only want to track
      // the resize if there's a change in the panel width
      if (didResize) {
        // midSize tracks the middle point of the current range of panel sizes
        const midSize = MAX_SIZE - (MAX_SIZE - panelSize) / 2;

        // `react-resizable-panels` does not provide an `onResizeDone` event, so on every onResize event,
        // we want to keep track of the currentResizeType so we don't overload eventing,
        // and we only want to call eventing when resize is finished, as indicated by the onPointerUp event (see handleResizeHandlePointerUp)
        if (width === MAX_SIZE) {
          setCurrentResizeType('resize_max');
        } else if (width === panelSize) {
          setCurrentResizeType('resize_min');
        } else if (width > midSize) {
          setCurrentResizeType('resize_above_mid');
        } else {
          setCurrentResizeType('resize_below_mid');
        }
      }
    },
    [panelSize]
  );

  /*
   * if/else conditional return would have been neater here but it causes the entire component tree
   * (more noticeable with video players) to unmount and remount when toggling toolbar states.
   * So using single return with conditional rendering to prevent video playback glitches by avoiding
   * unnecessary remounts of children components, though that means children components
   * are always inside PanelGroup.
   */
  return (
    <PanelGroup direction="horizontal">
      {/* I had to use inline style attribute because emotion css didn't work as expected. The main page content wasn't scrollable */}
      <Panel style={{ overflow: 'auto' }}>{children}</Panel>

      {shouldShowResizableToolbar && <SplitPanelResizeHandle onPointerUp={handleResizeHandlePointerUp} />}

      {shouldShowToolbar && (
        <Panel
          css={!shouldShowResizableToolbar && styles.coachPanelOverrides}
          key={'panel-size-' + panelSize}
          minSize={panelSize}
          defaultSize={panelSize}
          maxSize={MAX_SIZE}
          onResize={handlePanelResize}
        >
          <CourseToolBar showToolBar={false} />
        </Panel>
      )}
    </PanelGroup>
  );
};

export default WithResizableCourseToolBarWrapper;
